/**
 * Intent and synonym expansions for semantic search.
 * Maps user phrasing → terms that appear in experience metadata.
 */

/** Single-word / short phrase → related search terms. */
export const SYNONYM_EXPANSIONS = {
  phone: ['mobile', 'iphone', 'ipad', 'ios', 'android', 'browser', 'webar', 'ar', 'app store'],
  mobile: ['phone', 'iphone', 'ipad', 'ios', 'android', 'browser', 'webar', 'ar'],
  headset: ['vr', 'quest', 'meta quest', 'vision pro', 'pico', 'steamvr', 'psvr', 'headset', 'mr', 'mixed reality'],
  vr: ['virtual reality', 'headset', 'quest', 'meta quest', 'steamvr', 'immersive'],
  quest: ['meta quest', 'oculus', 'headset', 'vr'],
  browser: ['web', 'webxr', 'webar', 'mobile', 'desktop', 'youtube'],
  desktop: ['browser', 'laptop', 'pc', 'youtube', 'web'],
  ar: ['augmented reality', 'webar', 'mobile', 'phone', 'mixed reality', 'mr'],
  webar: ['ar', 'augmented reality', 'mobile', 'browser', 'phone'],
  webxr: ['browser', 'headset', 'immersive', 'webvr'],
  webvr: ['webxr', 'headset', 'browser', 'vr'],
  '360': ['360 video', 'youtube', 'immersive video', 'look around'],
  movie: ['film', 'cinema', 'video', 'documentary', 'cinematic', 'watch'],
  film: ['movie', 'cinema', 'video', 'documentary', 'watch'],
  game: ['play', 'interactive', 'games', 'gameplay'],
  play: ['game', 'interactive', 'games'],
  art: ['gallery', 'installation', 'museum', 'exhibition', 'digital art', 'artistic'],
  artistic: ['art', 'gallery', 'installation', 'museum', 'magical'],
  magical: ['art', 'installation', 'immersive', 'fantasy', 'teamlab'],
  museum: ['installation', 'exhibition', 'gallery', 'lbe', 'location-based', 'immersive museum'],
  gallery: ['art', 'museum', 'installation', 'exhibition'],
  exhibition: ['museum', 'gallery', 'installation', 'art'],
  scary: ['horror', 'haunted', 'thriller', 'intense', 'jump scare'],
  horror: ['scary', 'haunted', 'thriller', 'intense'],
  haunted: ['horror', 'scary', 'thriller'],
  thriller: ['horror', 'scary', 'intense'],
  relaxing: ['calm', 'meditation', 'wellness', 'nature', 'comfortable', 'sleep', 'tripp', 'peaceful'],
  calm: ['relaxing', 'meditation', 'wellness', 'comfortable', 'peaceful'],
  meditation: ['wellness', 'relaxing', 'tripp', 'calm', 'sleep'],
  wellness: ['meditation', 'relaxing', 'tripp', 'calm'],
  whale: ['ocean', 'underwater', 'marine', 'sea', 'water', 'nasa', 'aquatic'],
  whales: ['ocean', 'underwater', 'marine', 'sea', 'water', 'aquatic'],
  ocean: ['underwater', 'marine', 'sea', 'water', 'whale', 'nasa'],
  underwater: ['ocean', 'marine', 'nasa', 'pool', 'water'],
  kids: ['family', 'children', 'all ages', 'families', 'kid-friendly'],
  children: ['kids', 'family', 'all ages', 'families'],
  family: ['kids', 'children', 'families', 'all ages', 'pairs'],
  free: ['no cost', 'youtube', 'browser', 'admission'],
  cheap: ['free', 'affordable', 'budget'],
  solo: ['alone', 'single session', 'self-guided'],
  alone: ['solo', 'self-guided'],
  couple: ['pairs', 'date night', 'romantic', 'two'],
  couples: ['pairs', 'date night', 'romantic'],
  romantic: ['date night', 'couples', 'pairs'],
  social: ['multiplayer', 'metaverse', 'vrchat', 'hangout', 'nightlife'],
  nightlife: ['social', 'evening', 'comedy', 'club', 'live events'],
  evening: ['night', 'live events', 'nightlife', 'show'],
  friday: ['weekend', 'evening', 'night', 'live events', 'social'],
  weekend: ['friday', 'saturday', 'social', 'date night'],
  theater: ['theatre', 'interactive theater', 'live', 'performance', 'show', 'ferryman'],
  theatre: ['theater', 'interactive', 'live', 'performance'],
  comedy: ['funny', 'laugh', 'humor', 'humorous', 'open mic', 'punchline', 'entertaining'],
  funny: ['comedy', 'laugh', 'humor', 'humorous', 'punchline', 'open mic', 'entertaining', 'fun'],
  humor: ['comedy', 'funny', 'laugh', 'humorous', 'punchline'],
  humorous: ['comedy', 'funny', 'laugh', 'humor'],
  laugh: ['comedy', 'funny', 'humor', 'punchline', 'entertaining'],
  entertaining: ['comedy', 'fun', 'show', 'live events', 'social'],
  fun: ['casual', 'game', 'play', 'social', 'entertainment', 'comedy', 'funny'],
  friends: ['social', 'multiplayer', 'pairs', 'small groups', 'group', 'hangout', 'vrchat', 'together'],
  friend: ['friends', 'social', 'multiplayer', 'pairs', 'small groups', 'hangout'],
  hangout: ['social', 'vrchat', 'metaverse', 'multiplayer', 'friends'],
  group: ['small groups', 'pairs', 'families', 'multiplayer', 'social', 'groups'],
  groups: ['small groups', 'pairs', 'families', 'multiplayer', 'social'],
  together: ['pairs', 'small groups', 'multiplayer', 'social', 'friends'],
  immersive: ['xr', 'vr', 'ar', '360', 'installation', 'lbe'],
  interactive: ['game', 'play', 'explore', 'theater', 'participatory'],
  concert: ['music', 'live', 'performance', 'soapbox', 'amazevr'],
  music: ['concert', 'rhythm', 'beat saber', 'performance'],
  space: ['nasa', 'astronaut', 'sci-fi', 'underwater'],
  education: ['learn', 'museum', 'documentary', 'smithsonian'],
  learn: ['education', 'museum', 'documentary'],
  sports: ['xtadium', 'stadium', 'live', 'game'],
  sleep: ['relaxing', 'wellness', 'tripp', 'bed', 'wind down'],
  bed: ['sleep', 'relaxing', 'wellness', 'before bed'],
  home: ['at home', 'living room', 'browser', 'quest', 'anywhere'],
  tickets: ['ticket', 'venue', 'lbe', 'location-based', 'show'],
  venue: ['lbe', 'location-based', 'installation', 'museum', 'sphere'],
  los: ['los angeles', 'la', 'california'],
  angeles: ['los angeles', 'la', 'california'],
  la: ['los angeles', 'california'],
  vegas: ['las vegas', 'nevada'],
  miami: ['florida', 'superblue', 'teamlab'],
  dallas: ['texas', 'netflix house'],
  washington: ['dc', 'smithsonian', 'museum'],
  texas: ['dallas'],
  florida: ['miami'],
  nevada: ['las vegas', 'vegas'],
  california: ['los angeles', 'la'],
  netflix: ['netflix house', 'stranger things', 'squid game'],
  star: ['star wars', 'sci-fi'],
  wars: ['star wars', 'sci-fi'],
  birds: ['angry birds', 'game', 'ar'],
  angry: ['angry birds', 'game', 'ar'],
  vrchat: ['metaverse', 'social vr', 'social', 'world'],
  youtube: ['360', 'free', 'browser', 'video', 'pearl', 'help'],
  vision: ['vision pro', 'apple', 'headset'],
  pico: ['headset', 'vr'],
  steam: ['steamvr', 'pc vr', 'pcvr', 'headset'],
  psvr: ['playstation', 'ps vr', 'headset'],
  visionpro: ['vision pro', 'apple', 'headset'],
  installation: ['lbe', 'museum', 'art', 'immersive', 'venue'],
  lbe: ['location-based', 'venue', 'museum', 'installation', 'physical'],
  metaverse: ['social vr', 'vrchat', 'virtual world', 'social'],
  live: ['live events', 'concert', 'show', 'theater', 'scheduled'],
  beautiful: ['art', 'nature', 'cinematic', 'immersive', 'visual'],
  magical: ['art', 'teamlab', 'installation', 'immersive'],
  drive: ['location-based', 'venue', 'lbe', 'nearby'],
  nearby: ['location-based', 'lbe', 'venue', 'near me'],
  'near me': ['location-based', 'lbe', 'venue', 'nearby']
};

/**
 * Multi-word intent phrases → expanded terms and optional constraints.
 * @type {Array<{ test: RegExp, terms?: string[], constraints?: object }>}
 */
export const INTENT_PHRASES = [
  {
    test: /quest\s*apps?|meta\s*quest\s*apps?/i,
    terms: ['meta quest', 'quest', 'vr', 'headset', 'native app']
  },
  {
    test: /no\s*headset|without\s*a?\s*headset|don'?t\s*(own|have)\s*a?\s*headset|no\s*vr\s*headset/i,
    terms: ['mobile', 'browser', '360', 'webar', 'ar', 'phone', 'youtube', 'desktop'],
    constraints: { preferNoHeadset: true }
  },
  {
    test: /date\s*night|romantic\s*evening|couples?\s*night/i,
    terms: ['date night', 'pairs', 'romantic', 'couples', 'theater', 'lbe', 'concert', 'immersive']
  },
  {
    test: /friday\s*night|saturday\s*night|weekend\s*night/i,
    terms: ['social', 'live events', 'nightlife', 'comedy', 'concert', 'show', 'evening']
  },
  {
    test: /before\s*bed|wind\s*down|help\s*me\s*sleep/i,
    terms: ['relaxing', 'wellness', 'meditation', 'tripp', 'calm', 'sleep', 'comfortable']
  },
  {
    test: /after\s*work|decompress|stress\s*relief/i,
    terms: ['relaxing', 'wellness', 'meditation', 'calm', 'comfortable', 'solo']
  },
  {
    test: /something\s*for\s*kids|kid[\s-]?friendly|with\s*children|family\s*friendly/i,
    terms: ['family', 'families', 'children', 'kids', 'all ages', 'all-ages']
  },
  {
    test: /free\s*ar|ar\s*free|free\s*experiences?/i,
    terms: ['free', 'ar', 'webar', 'augmented reality', 'mobile']
  },
  {
    test: /immersive\s*museums?|museum\s*experience/i,
    terms: ['museum', 'installation', 'lbe', 'location-based', 'exhibition', 'gallery', 'teamlab', 'smithsonian']
  },
  {
    test: /interactive\s*theater|live\s*theater|theatre/i,
    terms: ['theater', 'theatre', 'interactive', 'ferryman', 'uncanny alley', 'live events', 'performance']
  },
  {
    test: /works?\s*on\s*(my\s*)?phone|phone\s*only|on\s*my\s*phone/i,
    terms: ['mobile', 'phone', 'ios', 'android', 'browser', 'webar', 'ar', 'app store']
  },
  {
    test: /from\s*home|at\s*home|in\s*my\s*living\s*room/i,
    terms: ['at home', 'home', 'browser', 'quest', 'mobile', 'anywhere', 'living room']
  },
  {
    test: /los\s*angeles|\bl\.?a\.?\b/i,
    terms: ['los angeles', 'la', 'california'],
    constraints: { locationHint: 'los angeles' }
  },
  {
    test: /las\s*vegas|\bvegas\b/i,
    terms: ['las vegas', 'nevada', 'vegas'],
    constraints: { locationHint: 'las vegas' }
  },
  {
    test: /\bmiami\b/i,
    terms: ['miami', 'florida'],
    constraints: { locationHint: 'miami' }
  },
  {
    test: /\bdallas\b/i,
    terms: ['dallas', 'texas'],
    constraints: { locationHint: 'dallas' }
  },
  {
    test: /under\s*\$?\s*(\d+)|less\s*than\s*\$?\s*(\d+)|budget|cheap|affordable/i,
    constraints: { maxPrice: true }
  },
  {
    test: /\bfree\b/i,
    terms: ['free'],
    constraints: { requireFree: true }
  },
  {
    test: /scary|horror|haunted|spooky|creepy/i,
    terms: ['horror', 'scary', 'haunted', 'thriller', 'intense', 'conjuring', 'blumhouse']
  },
  {
    test: /relaxing|calm|peaceful|chill|meditat/i,
    terms: ['relaxing', 'wellness', 'meditation', 'calm', 'nature', 'comfortable', 'tripp']
  },
  {
    test: /solo|by\s*myself|alone\b/i,
    terms: ['solo', 'self-guided', 'single session']
  },
  {
    test: /with\s*(my\s+)?friends?|hang\s*out|buddy|buddies|\bfriends?\b/i,
    terms: ['social', 'multiplayer', 'pairs', 'small groups', 'friends', 'hangout', 'group', 'vrchat', 'together']
  },
  {
    test: /something\s+funny|funny|humou?rous?|make\s+me\s+laugh|\blaugh\b|silly|hilarious/i,
    terms: ['comedy', 'funny', 'laugh', 'humor', 'punchline', 'entertaining', 'fun', 'open mic']
  },
  {
    test: /beautiful|cinematic|gorgeous|stunning/i,
    terms: ['art', 'nature', 'music', 'immersive', 'teamlab', 'cinematic']
  },
  {
    test: /game|gaming|playable/i,
    terms: ['game', 'games', 'play', 'interactive', 'beat saber', 'angry birds']
  },
  {
    test: /film|movie|video|watch/i,
    terms: ['movies', 'watch', 'cinema', '360', 'video', 'documentary']
  }
];

/** Genre / content-type aliases. */
export const CONTENT_TYPE_ALIASES = {
  movie: 'movies',
  movies: 'movies',
  film: 'movies',
  films: 'movies',
  video: 'movies',
  documentary: 'movies',
  game: 'games',
  games: 'games',
  gaming: 'games',
  education: 'education',
  educational: 'education',
  learn: 'education',
  travel: 'travel',
  live: 'live-events',
  concert: 'live-events',
  concerts: 'live-events',
  events: 'live-events'
};

/** Category aliases. */
export const CATEGORY_ALIASES = {
  vr: 'virtual-reality',
  'virtual reality': 'virtual-reality',
  ar: 'augmented-reality',
  'augmented reality': 'augmented-reality',
  '360': '360-video',
  '360 video': '360-video',
  lbe: 'location-based',
  'location based': 'location-based',
  museum: 'location-based',
  museums: 'location-based',
  metaverse: 'metaverse',
  'social vr': 'metaverse'
};

/** Conversational prefixes stripped only after full-query intent scan. */
export const QUERY_FILLER_PREFIXES = [
  /^something\s+(to\s+do\s+)?/i,
  /^anything\s+(to\s+do\s+)?/i,
  /^looking\s+for\s+/i,
  /^i\s+(want|need|am\s+looking\s+for)\s+/i,
  /^show\s+me\s+/i,
  /^find\s+me\s+/i,
  /^give\s+me\s+/i,
  /^(some|a)\s+/i
];

/**
 * Expand a term using direct synonyms and reverse lookups
 * (e.g. "funny" → comedy, because comedy expands to funny).
 */
export function expandSynonymTerm(term) {
  const results = new Set([term]);
  const synonyms = SYNONYM_EXPANSIONS[term];
  if (synonyms) synonyms.forEach((t) => results.add(t));

  for (const [key, values] of Object.entries(SYNONYM_EXPANSIONS)) {
    if (key === term || values.includes(term)) {
      results.add(key);
      values.forEach((v) => results.add(v));
    }
  }
  return [...results];
}

/**
 * Scan the full normalized query for any synonym key appearing as a phrase or word.
 */
export function expandTermsFromFullQuery(normalized) {
  const terms = new Set();
  if (!normalized) return [];

  for (const [key, values] of Object.entries(SYNONYM_EXPANSIONS)) {
    const matches = key.includes(' ')
      ? normalized.includes(key)
      : new RegExp(`\\b${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`).test(normalized);
    if (matches) {
      terms.add(key);
      values.forEach((v) => terms.add(v));
    }
  }
  return [...terms];
}

/** Strip conversational filler to reveal the user's actual intent. */
export function extractIntentCore(normalized) {
  let core = normalized.trim();
  if (!core) return '';

  for (let pass = 0; pass < 4; pass += 1) {
    let changed = false;
    for (const prefix of QUERY_FILLER_PREFIXES) {
      const next = core.replace(prefix, '').trim();
      if (next !== core) {
        core = next;
        changed = true;
      }
    }
    if (!changed) break;
  }
  return core || normalized;
}
